/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { EventEmitter, Injectable } from "@angular/core";
import { ReceiverTypes } from "@app/entity/components/company-settings/central-stations/central-station.model";
import { entityRoutePaths } from "@app/entity/entity.routes";
import { EntityInfo } from "@app/entity/model/entityinfo";
import { EntityInfoService } from "@app/entity/services/entityinfo.service";
import { RouterActions } from "@app/root-store";
import { SnackbarActions } from "@app/root-store/actions";
import { LoadingState } from "@app/root-store/api-result.state";
import { State } from "@app/root-store/app.state";
import {
    ClareStatuses,
    disabledClareStatus,
    offlineClareStatus,
    onlineClareStatus,
    unknownClareStatus,
} from "@app/shared/model/clare-status.model";
import { HttpResponseHandler } from "@app/shared/services/http-response-handler";
import { Action, Store, select } from "@ngrx/store";
import { Observable, Subject, combineLatest, of, pipe } from "rxjs";
import { distinctUntilChanged, filter, map, switchMap, take, takeUntil, tap, withLatestFrom } from "rxjs/operators";
import { AlulaDevice } from "../model/alula-device";
import { AlulaReceiver } from "../model/alula-receiver";
import { AlulaReceiverBind } from "../model/alula-receiver-bind";
import { AlulaReceiverGroup, CentralStation } from "../model/alula-receiver-group";
import { AlulaSelectors } from "../store";
import { AlulaReceiverActions, AlulaReceiverBindActions, AlulaReceiverGroupActions } from "../store/actions";

@Injectable({
    providedIn: "root",
})
export class AlulaReceiverService {
    private readonly companySettingsRoute = "companysettings";
    private readonly centralStationsRoute = "central-stations";

    goToReceiversList(entityId: number) {
        this.store.dispatch(
            RouterActions.routerGo({
                path: [`/${entityRoutePaths.root}${entityRoutePaths.edit}`, entityId, this.companySettingsRoute, this.centralStationsRoute],
            })
        );
    }

    goToAddReceiverGroup(entityId, receiverGroupId) {
        const path = receiverGroupId
            ? [
                  `/${entityRoutePaths.root}${entityRoutePaths.edit}`,
                  entityId,
                  this.companySettingsRoute,
                  this.centralStationsRoute,
                  "edit",
                  receiverGroupId,
              ]
            : [`/${entityRoutePaths.root}${entityRoutePaths.edit}`, entityId, this.companySettingsRoute, this.centralStationsRoute, "add"];
        this.store.dispatch(
            RouterActions.routerGo({
                path,
            })
        );
    }

    goToCompanySettings(entityId) {
        this.store.dispatch(
            RouterActions.routerGo({
                path: [`/${entityRoutePaths.root}${entityRoutePaths.edit}`, entityId, this.companySettingsRoute],
            })
        );
    }

    goToCentralStationProjectList(entityId, receiverGroupId) {
        this.store.dispatch(
            RouterActions.routerGo({
                path: [
                    `/${entityRoutePaths.root}${entityRoutePaths.edit}`,
                    entityId,
                    this.companySettingsRoute,
                    this.centralStationsRoute,
                    "edit",
                    receiverGroupId,
                    "devices",
                ],
            })
        );
    }

    getAlulaReceivers(dealerId: string, noDispatch = false) {
        return this.store.pipe(
            select(AlulaSelectors.getAlulaReceivers()),
            distinctUntilChanged(),
            map(receiversState => {
                if (receiversState?.callState === LoadingState.init && !noDispatch) {
                    this.store.dispatch(AlulaReceiverActions.load({ dealerId }));
                }
                return receiversState?.result;
            })
        );
    }
    getAlulaReceiverById(id: string) {
        return this.store.pipe(select(AlulaSelectors.getAlulaReceiverById(id)));
    }

    dispatchCreateAlulaReceiver(receiver: AlulaReceiver, callback?: EventEmitter<any>) {
        return this.store.dispatch(AlulaReceiverActions.create({ receiver, callback }));
    }
    dispatchCreateAlulaReceiverAndBind(receiver: AlulaReceiver, bind: AlulaReceiverBind, callback?: EventEmitter<any>) {
        return this.store.dispatch(AlulaReceiverActions.create({ receiver, bind, callback }));
    }
    dispatchUpdateAlulaReceiver(receiver: AlulaReceiver, callback?: EventEmitter<any>) {
        return this.store.dispatch(AlulaReceiverActions.update({ receiver, callback }));
    }

    getAlulaReceiverGroups(dealerId: string, noDispatch = false) {
        return this.store.pipe(
            select(AlulaSelectors.getAlulaReceiverGroups()),
            distinctUntilChanged(),
            tap(groupsState => {
                if (groupsState?.callState === LoadingState.init && !noDispatch) {
                    this.store.dispatch(AlulaReceiverGroupActions.load({ dealerId }));
                }
            }),
            filter(groupsState => groupsState?.callState === LoadingState.loaded),
            map(groupsState => groupsState?.result)
        );
    }

    getAlulaReceiverGroupById(id: string) {
        return this.store.pipe(select(AlulaSelectors.getAlulaReceiverGroupById(id)));
    }

    dispatchCreateAlulaReceiverGroup(receiverGroup: AlulaReceiverGroup, callback?: EventEmitter<any>) {
        return this.store.dispatch(AlulaReceiverGroupActions.create({ receiverGroup, callback }));
    }

    dispatchUpdateAlulaReceiverGroup(receiverGroup: AlulaReceiverGroup, callback?: EventEmitter<any>) {
        return this.store.dispatch(AlulaReceiverGroupActions.update({ receiverGroup, callback }));
    }

    dispatchDeleteAlulaReceiverGroup(receiverGroup: AlulaReceiverGroup, logData: any) {
        return this.store.dispatch(AlulaReceiverGroupActions.deleteConfirmation({ receiverGroup, logData }));
    }

    getAlulaReceiverBindsArray(dealerId) {
        return this.store.pipe(
            select(AlulaSelectors.getAlulaReceiverBindsArray),
            tap(binds => {
                if (binds?.callState === LoadingState.init) {
                    this.store.dispatch(AlulaReceiverBindActions.load({ dealerId }));
                }
            })
        );
    }

    getAlulaReceiverBinds(dealerId: string, noDispatch = false) {
        return this.store.pipe(
            select(AlulaSelectors.getAlulaReceiverBinds),
            map(binds => {
                if (binds?.callState === LoadingState.init && !noDispatch) {
                    this.store.dispatch(AlulaReceiverBindActions.load({ dealerId }));
                }
                return binds?.result;
            })
        );
    }
    getAlulaReceiverBindById(groupId: string, bindId: string) {
        return this.store.pipe(select(AlulaSelectors.getAlulaReceiverBindById(groupId, bindId)));
    }
    dispatchCreateAlulaReceiverBind(receiverBind: AlulaReceiverBind, callback?: EventEmitter<any>) {
        return this.store.dispatch(AlulaReceiverBindActions.create({ receiverBind, callback }));
    }
    dispatchUpdateAlulaReceiverBind(receiverBind: AlulaReceiverBind, callback?: EventEmitter<any>) {
        return this.store.dispatch(AlulaReceiverBindActions.update({ receiverBind, callback }));
    }
    dispatchDeleteAlulaReceiverBind(receiverBind: AlulaReceiverBind, receiver?: AlulaReceiver, callback?: EventEmitter<any>) {
        return this.store.dispatch(AlulaReceiverBindActions.deleteItem({ receiverBind, receiver, callback }));
    }

    createReceiversPipe(arns: any[], dealerId: string) {
        return pipe(
            map(receivers => {
                const receiversArr = Object.values(receivers);
                arns.forEach(arn => {
                    if (
                        !receiversArr.find(
                            receiver =>
                                AlulaDevice.unformatPhoneNoCountryCode(receiver?.path) === AlulaDevice.unformatPhoneNoCountryCode(arn.phone)
                        )
                    ) {
                        const arnLegacyReceiver = this.buildReceiverFromArn(arn, dealerId);
                        this.dispatchCreateAlulaReceiver(arnLegacyReceiver);
                    }
                });
            }),
            switchMap(() => this.getAlulaReceivers(dealerId)),
            filter(receivers => arns.every(arn => Object.values(receivers).find(receiver => this.isMatchingPhoneAndPath(receiver, arn))))
        );
    }

    createReceiverGroupAndBindsPipe(arns: any[], dealerId: string) {
        return pipe(
            switchMap(receivers =>
                this.getAlulaReceiverBindsArray(dealerId).pipe(
                    filter(bindsState => !!(bindsState.callState === LoadingState.loaded && receivers)),
                    map(bindsState => bindsState.result),
                    take(1),
                    tap(binds => {
                        Object.values(receivers).forEach(receiver => {
                            const bindExists = binds.some(bind => bind?.receiverId === receiver.id);
                            const isConvertedArn = arns.some(arn => this.isMatchingPhoneAndPath(receiver, arn));

                            if (!bindExists && isConvertedArn) {
                                const newReceiverGroup = this.buildReceiverGroup(receiver, dealerId);
                                const newCallback = new EventEmitter<any>();
                                newCallback
                                    .pipe(
                                        take(1),
                                        map(groupAction => groupAction.group),
                                        tap(group => {
                                            const newReceiverBind = this.buildReceiverBind(receiver, group);
                                            this.dispatchCreateAlulaReceiverBind(newReceiverBind);
                                        })
                                    )
                                    .subscribe();
                                this.dispatchCreateAlulaReceiverGroup(newReceiverGroup, newCallback);
                            }
                        });
                    })
                )
            ),
            switchMap(() => this.getAlulaReceiverBindsArray(dealerId).pipe(map(receiverState => receiverState.result)))
        );
    }

    isMatchingPhoneAndPath(receiver: AlulaReceiver, arn) {
        return AlulaDevice.unformatPhoneNoCountryCode(receiver.path) === AlulaDevice.unformatPhoneNoCountryCode(arn.phone);
    }

    convertToReceivers(arns: any[], dealerId: string, conversionComplete: { emit: (anrs: any[]) => void }, unsubscribe$: Subject<any>) {
        this.getAlulaReceivers(dealerId)
            .pipe(
                filter(receivers => !!receivers),
                take(1),
                this.createReceiversPipe(arns, dealerId),
                this.createReceiverGroupAndBindsPipe(arns, dealerId),
                withLatestFrom(this.getAlulaReceivers(dealerId)),
                // get the receivers here from the store and make sure that for each receiver that matches an arn phone, we have a bind
                filter(([binds, receivers]) => {
                    // filter down the receivers to ones that match an ARN
                    const matchingReceivers = Object.values(receivers).filter(receiver =>
                        arns.some(arn => this.isMatchingPhoneAndPath(receiver, arn))
                    );
                    // make sure there is a bind for every matched receiver
                    const matchingBinds = binds.filter(bind => matchingReceivers.some(receiver => receiver.id === bind.receiverId));
                    return matchingBinds.length >= arns.length;
                }),
                tap(() => {
                    this.deleteARNs(arns, unsubscribe$, conversionComplete);
                }),
                take(1)
            )
            .subscribe();
    }

    deleteARNs(arns: EntityInfo[], unsubscribe$: Subject<any>, conversionComplete) {
        this.entityInfoService
            .selectEntityInfoListByTypeId(1)
            .pipe(takeUntil(unsubscribe$))
            .subscribe(alarmReportingNumbers => {
                if (alarmReportingNumbers.length >= 1) {
                    this.entityInfoService.triggerDeleteEntityInfoSilentAction(alarmReportingNumbers[0]);
                } else {
                    conversionComplete.emit(arns);
                }
            });
    }

    buildReceiverFromArn(arn, dealerId: string): AlulaReceiver {
        const unFormattedArnPhone = AlulaDevice.unformatPhoneNoCountryCode(arn.phone);
        const formattedArnPhone = AlulaDevice.formatPhone(unFormattedArnPhone);
        return {
            dealerId,
            name: arn.name || "",
            receiverType: ReceiverTypes.pstn,
            path: formattedArnPhone || "(000)000-0000",
            description: "Standard Phone Line",
        };
    }

    buildReceiverGroup(receiver: AlulaReceiver | CentralStation, dealerId: string): AlulaReceiverGroup {
        return {
            dealerId,
            name: receiver?.name || "",
            description: `this receiver group converted from alarm reporting number: ${receiver?.name || ""} `,
        };
    }

    buildReceiverBind(receiver: AlulaReceiver, group: AlulaReceiverGroup | CentralStation, priority = 0): AlulaReceiverBind {
        return {
            dealerId: group.dealerId,
            priority: priority.toString(),
            receiverGroupId: group.id,
            receiverId: receiver?.id,
        };
    }

    hasReceiverUpdated(prevReceiver: AlulaReceiver, currReceiver: AlulaReceiver) {
        let hasChanged = false;

        for (const prop in prevReceiver) {
            if (prevReceiver[prop] !== currReceiver[prop]) {
                hasChanged = true;
            }
        }

        return hasChanged;
    }

    getReceiverStatus(receiver: AlulaReceiver) {
        if (receiver?.disabled) {
            return disabledClareStatus;
        }

        switch (receiver?.status) {
            case ClareStatuses.Offline.toLowerCase():
                return offlineClareStatus;

            case ClareStatuses.Online.toLowerCase():
                return onlineClareStatus;

            default:
                return unknownClareStatus;
        }
    }

    //Central Station Management Area//
    getCentralStationsList(dealerId: string, noDispatch = false): Observable<CentralStation[]> {
        if (!!dealerId) {
            return combineLatest({
                receivers: this.getAlulaReceivers(dealerId, noDispatch),
                groups: this.getAlulaReceiverGroups(dealerId, noDispatch),
                binds: this.getAlulaReceiverBinds(dealerId, noDispatch),
            }).pipe(
                filter(({ receivers, groups, binds }) => !!(receivers && groups && binds)),
                map(({ receivers, groups, binds }) =>
                    groups
                        ?.map(group => {
                            const centralStation: CentralStation = {
                                dealerId,
                                name: group.name || "",
                                id: group.id,
                                ipCommPath1: "",
                                ipCommPath1Status: null,
                                ipCommPath2: "",
                                ipCommPath2Status: null,
                                phoneCommPath: "",
                                phoneCommStatus: null,
                                receivers: [],
                                binds: binds?.[group.id] || [],
                            };
                            let priorityCounter = 0;

                            binds?.[group.id]?.forEach(bind => {
                                const receiver = receivers[bind?.receiverId];
                                switch (receiver?.receiverType) {
                                    case ReceiverTypes.matrix:
                                    case ReceiverTypes.stages:
                                    case ReceiverTypes.sgtcp:
                                        if (priorityCounter < 2) {
                                            centralStation["ipCommPath" + (priorityCounter + 1)] = receiver.path;
                                            centralStation["path" + (priorityCounter + 1) + "Protocol"] = receiver.receiverType;
                                            centralStation["ipCommPath" + (priorityCounter + 1) + "Status"] =
                                                this.getReceiverStatus(receiver);
                                            centralStation.receivers[priorityCounter] = receiver;
                                            priorityCounter++;
                                        }
                                        break;
                                    case ReceiverTypes.pstn:
                                        centralStation.phoneCommPath = receiver.path;
                                        centralStation.phoneCommStatus = this.getReceiverStatus(receiver);
                                        centralStation.receivers[2] = receiver;
                                        break;
                                }
                            });
                            return centralStation;
                        })
                        .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
                )
            );
        }

        return of([]);
    }

    createCentralStation(centralStation: CentralStation, callback: EventEmitter<Action>) {
        if (centralStation) {
            const newGroup = this.buildReceiverGroup(centralStation, centralStation.dealerId);
            newGroup.description = centralStation.name;

            this.dispatchCreateAlulaReceiverGroup(newGroup, callback);
            callback
                .pipe(
                    tap((action: any) => this.createCentralStationReceivers(action?.group, centralStation.receivers, callback)),
                    take(1)
                )
                .subscribe();
        }
    }

    createCentralStationReceivers(receiverGroup: AlulaReceiverGroup, receivers: AlulaReceiver[], callback) {
        if (receiverGroup?.id) {
            let priorityCount = 0;

            receivers.forEach(receiver => {
                if (receiver) {
                    const receiverBind = this.buildReceiverBind(receiver, receiverGroup, priorityCount);
                    this.dispatchCreateAlulaReceiverAndBind(receiver, receiverBind, callback);

                    priorityCount++;
                }
            });
        }
    }

    doesHaveValidReceivers(centralStation: CentralStation): { hasValidReceivers: boolean; hasInvalidReceivers: boolean } {
        return {
            hasValidReceivers: centralStation?.receivers.some(receiver => !receiver?.disabled && receiver.status === "online"),
            hasInvalidReceivers: centralStation?.receivers.some(
                receiver => receiver?.disabled || (receiver?.receiverType !== "PSTN" && receiver?.status !== "online")
            ),
        };
    }

    deleteCentralStation(centralStation: CentralStation) {
        //This should really dispatch a delete action, not make a db call.
        // return this.alulaDatabaseService.deleteAlulaReceiverGroup(centralStation?.id).pipe(
        //     take(1),
        //     exhaustMap(() => {
        //         const deleteRcvrs = centralStation.receivers.map(rcvr => this.alulaDatabaseService.deleteAlulaReceiver(rcvr.id));
        //         return combineLatest([...deleteRcvrs]);
        //     }),
        //     catchError(DataServiceError.handleError())
        // );
    }

    clearCentralStations() {
        this.store.dispatch(AlulaReceiverGroupActions.clear());
    }

    dispatchError(message: string) {
        this.store.dispatch(
            SnackbarActions.openSnackbar({
                message: new HttpResponseHandler(null, null, message, true),
            })
        );
    }

    constructor(private readonly store: Store<State>, private readonly entityInfoService: EntityInfoService) {}
}
